import { useCallback, useRef, useState } from "react";
import { Form, Flex, Alert } from "antd";
import { observer } from "mobx-react-lite";

import { createReport, getReportById } from "services";
import { _isRequiredDateRange, dateISOFormat } from "utils";
import { LinkButton, CustomButton, DefaultRangeDateInput } from "ui";
import { useStores } from "stores";
import { COMPANY_ROLE_ENUM, REPORT_TYPE_ENUM } from "interfaces";
import { ReactComponent as DownloadIcon } from "images/downloadIcon.svg";
import { FORM_VALIDATION_DEBOUNCE } from "constants/form";

export const FeeReportForm = observer(() => {
  const { user, companyCard } = useStores();

  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const [downloadLink, setDownloadLink] = useState("");
  const [filename, setFilename] = useState("");

  const [form] = Form.useForm();

  const userCompanyRole = user.userInfo?.company_role;
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const getFileById = useCallback((id: string) => {
    if (intervalRef.current) clearInterval(intervalRef.current);

    intervalRef.current = setInterval(async () => {
      try {
        const data = await getReportById({ id });

        if (data.errorMessage) {
          setError(data.errorMessage);
          setSuccess(false);
          setLoading(false);
          clearInterval(intervalRef.current!);
          intervalRef.current = null;
        } else {
          setSuccess(true);
          if (data.doc) {
            setDownloadLink(data.doc.url);
            setFilename(data.doc.docName);
          }
          clearInterval(intervalRef.current!);
          intervalRef.current = null;
          setLoading(false);
        }
      } catch (e: any) {
        if (e.status !== 404) {
          clearInterval(intervalRef.current!);
          intervalRef.current = null;
          setError(e.title || e);
          setLoading(false);
          setSuccess(false);
        }
      }
    }, 3000);
  }, []);

  const onFinish = async () => {
    setSuccess(false);
    setLoading(true);
    setError("");
    const values = form.getFieldsValue();
    const selectedDate = values.dates;

    const formData = JSON.stringify({
      lpCompanyRegNumber:
        userCompanyRole === COMPANY_ROLE_ENUM.LIQUIDITY_PROVIDER
          ? user.userInfo!.company_reg_num
          : companyCard.card.companyRegistrationNumber,
      startDate: dateISOFormat(selectedDate[0], false),
      endDate: dateISOFormat(selectedDate[1], false),
    });

    await createReport(formData, REPORT_TYPE_ENUM.FEE_CUR)
      .then((id) => {
        getFileById(id);
      })
      .catch((e) => {
        setError(e.title || e);
      });
  };

  return (
    <Flex vertical gap="large">
      <Form layout="vertical" onFinish={onFinish} form={form} initialValues={{ dates: null }}>
        <Flex>
          <Form.Item
            name="dates"
            rules={[_isRequiredDateRange("Укажите датy")]}
            validateDebounce={FORM_VALIDATION_DEBOUNCE}
            label="Выберите дату"
            className="full-width"
          >
            <DefaultRangeDateInput disabled={loading || success} />
          </Form.Item>
        </Flex>

        <CustomButton htmlType="submit" loading={loading} disabled={success}>
          Сформировать
        </CustomButton>
      </Form>
      {success && downloadLink && (
        <LinkButton icon={<DownloadIcon />} href={downloadLink} download={filename}>
          {filename}
        </LinkButton>
      )}
      {error && <Alert message={"Ошибка"} description={error} type={"error"} className="full-width" showIcon />}
    </Flex>
  );
});
